/* eslint-disable react/jsx-props-no-spreading */

import {
  Redirect, Route, makeRouteConfig,
} from 'found';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import { graphql } from 'react-relay';

import { handleApplicationErrors } from 'src/auth/errorHandler';
// Admin
import {
  AdminPortfolioCreate,
  AdminPropertyList,
  AdminPropertyShow,
  AdminUserInviteAdmin,
  AdminUserInviteCustomer,
} from 'src/components/Admin';
// Authentication
import {
  AuthConfirmEmail,
  AuthErrorPage,
  AuthForgotPassword,
  AuthLogin,
  AuthLogout,
  AuthRegister,
  AuthResetPassword,
} from 'src/components/Auth';
// Layouts
import HTTP403 from 'src/components/HTTP403';
import AdminLayout from 'src/components/Layout/AdminLayout';
import AnonymousLayout from 'src/components/Layout/AnonymousLayout';
import CustomerLayout from 'src/components/Layout/CustomerLayout';
import CustomerPortfolioLayout from 'src/components/Layout/CustomerLayout/CustomerPortfolioLayout';
import CustomerPropertyLayout from 'src/components/Layout/CustomerLayout/CustomerPropertyLayout';
// Loading
import Loading from 'src/components/Loading';
// Config.
import { APIConfig } from 'src/config';
import {
  AUTH_ERROR_USER_DEACTIVATED, DEFAULT_LIST_PAGE_SIZE,
  TradeRuleStates, TradeTypes,
} from 'src/util/constants';
import { LocaleUrlSynchronizer } from 'src/util/i18n/LocaleUrlSynchronizer';

import { adminCommunityListRoute, adminCommunityListRouteQuery } from './AdminCommunityListRoute';
import { adminCommunityShowRoute, adminCommunityShowRouteQuery } from './AdminCommunityShowRoute';
import { adminMeterListRoute, adminMeterListRouteQuery } from './AdminMeterListRoute';
import { adminMeterShowRoute, adminMeterShowRouteQuery } from './AdminMeterShowRoute';
import { adminMeterTradeRulesRoute, adminMeterTradeRulesRouteQuery } from './AdminMeterTradeRulesRoute';
import { adminPortfolioEditRoute, adminPortfolioEditRouteQuery } from './AdminPortfolioEditRoute';
import { adminPortfolioListRoute, adminPortfolioListRouteQuery } from './AdminPortfolioListRoute';
import { adminPortfolioPropertyMemberCreateRoute, adminPortfolioPropertyMemberCreateRouteQuery } from './AdminPortfolioPropertyMemberCreateRoute';
import { adminPortfolioPropertyMemberRetireRoute, adminPortfolioPropertyMemberRetireRouteQuery } from './AdminPortfolioPropertyMemberRetireRoute';
import { adminPortfolioPropertyMemberShowRoute, adminPortfolioPropertyMemberShowRouteQuery } from './AdminPortfolioPropertyMemberShowRoute';
import { adminPortfolioRetireRoute, adminPortfolioRetireRouteQuery } from './AdminPortfolioRetireRoute';
import { adminPortfolioShowRoute, adminPortfolioShowRouteQuery } from './AdminPortfolioShowRoute';
import { adminPortfolioUserMemberCreateRoute, adminPortfolioUserMemberCreateRouteQuery } from './AdminPortfolioUserMemberCreateRoute';
import { adminPortfolioUserMemberEditRoute, adminPortfolioUserMemberEditRouteQuery } from './AdminPortfolioUserMemberEditRoute';
import { adminPortfolioUserMemberShowRoute, adminPortfolioUserMemberShowRouteQuery } from './AdminPortfolioUserMemberShowRoute';
import { adminPrimaryBillingPointListRoute, adminPrimaryBillingPointListRouteQuery } from './AdminPrimaryBillingPointListRoute';
import { adminPrimaryBillingPointShowRoute, adminPrimaryBillingPointShowRouteQuery } from './AdminPrimaryBillingPointShowRoute';
import { adminPropertyUserAddRoute, adminPropertyUserAddRouteQuery } from './AdminPropertyUserAddRoute';
import { adminPropertyUserListRoute, adminPropertyUserListRouteQuery } from './AdminPropertyUserListRoute';
import { adminPropertyUserShowRoute, adminPropertyUserShowRouteQuery } from './AdminPropertyUserShowRoute';
import { adminRetirePortfolioUserMemberRoute, adminRetirePortfolioUserMemberRouteQuery } from './AdminRetirePortfolioUserMemberRoute';
import { adminStandingChargeListRoute, adminStandingChargeListRouteQuery } from './AdminStandingChargeListRoute';
import { adminStandingChargeShowRoute, adminStandingChargeShowRouteQuery } from './AdminStandingChargeShowRoute';
import { adminTariffListRoute, adminTariffListRouteQuery } from './AdminTariffListRoute';
import { adminTariffShowRoute, adminTariffShowRouteQuery } from './AdminTariffShowRoute';
import { adminTradeRuleListRoute, adminTradeRuleListRouteQuery } from './AdminTradeRuleListRoute';
import { adminTradeRuleShowRoute, adminTradeRuleShowRouteQuery } from './AdminTradeRuleShowRoute';
import { adminUserListRoute, adminUserListRouteQuery } from './AdminUserListRoute';
import { adminUserShowRoute, adminUserShowRouteQuery } from './AdminUserShowRoute';
import { adminVolumeChargeListRoute, adminVolumeChargeListRouteQuery } from './AdminVolumeChargeListRoute';
import { adminVolumeChargeShowRoute, adminVolumeChargeShowRouteQuery } from './AdminVolumeChargeShowRoute';
import AuthenticatedRoute from './AuthenticatedRoute';
import { portfolioListRoute, portfolioListRouteQuery } from './PortfolioListRoute';
import { portfolioPropertyMemberListRoute, portfolioPropertyMemberListRouteQuery } from './PortfolioPropertyMemberListRoute';
import { portfolioShowRoute, portfolioShowRouteQuery } from './PortfolioShowRoute';
import { portfolioUserMemberListRoute, portfolioUserMemberListRouteQuery } from './PortfolioUserMemberListRoute';
import { profileSettingsRoute, profileSettingsRouteQuery } from './ProfileSettingsRoute';
import { propertyListRoute, propertyListRouteQuery } from './PropertyListRoute';
import { propertySettingsRoute, propertySettingsRouteQuery } from './PropertySettingsRoute';
import { propertyShowRoute, propertyShowRouteQuery } from './PropertyShowRoute';
import { tariffListRoute, tariffListRouteQuery } from './TariffListRoute';
import { tradeListRoute, tradeListRouteQuery } from './TradeListRoute';
import { tradeRuleActiveRoute, tradeRuleActiveRouteQuery } from './TradeRuleActiveRoute';
import { tradeRuleCustomerProposalsRoute, tradeRuleCustomerProposalsRouteQuery } from './TradeRuleCustomerProposalsRoute';
import { tradeRuleCustomerShowRoute, tradeRuleCustomerShowRouteQuery } from './TradeRuleCustomerShowRoute';
import { tradeRuleListRoute, tradeRuleListRouteQuery } from './TradeRuleListRoute';
import { tradeRulePrioritizeNominatedRoute, tradeRulePrioritizeNominatedRouteQuery } from './TradeRulePrioritizeNominatedRoute';
import { tradeRuleProposalNominatedGeneralisedRoute, tradeRuleProposalNominatedGeneralisedRouteQuery } from './TradeRuleProposalNominatedGeneralisedRoute';
import { tradeRuleProposeNominatedRoute, tradeRuleProposeNominatedRouteQuery } from './TradeRuleProposeNominatedRoute';
import { tradeRuleSetCommunityRoute, tradeRuleSetCommunityRouteQuery } from './TradeRuleSetCommunityRoute';
import { tradeRuleShowRoute, tradeRuleShowRouteQuery } from './TradeRuleShowRoute';
import buildPortfolioPropertyMemberListRetryVariables from './helpers/buildPortfolioPropertyMemberListRetryVariables';
import buildPortfolioUserMemberListRetryVariables from './helpers/buildPortfolioUserMemberListRetryVariables';
import FlashProvider from '../components/Flashes/FlashesProvider';

/**
 * Mounts application-wide providers that require access to Found router context.
 * @param {object} props component props.
 * @param {React.ReactNode} props.children route children.
 * @returns {React.ReactElement} application provider tree.
 */
function ApplicationProviders({ children }) {
  return (
    <LocaleUrlSynchronizer>
      <FlashProvider>{children}</FlashProvider>
    </LocaleUrlSynchronizer>
  );
}

ApplicationProviders.propTypes = {
  children: PropTypes.node.isRequired,
};

export default makeRouteConfig(
  <Route Component={ApplicationProviders}>

    <Route path="/home" Component={AnonymousLayout} />

    <Route path="/confirm-email" Component={AnonymousLayout}>
      <Route path=":token" Component={AuthConfirmEmail} />
    </Route>

    <Route path="/login" Component={AnonymousLayout}>
      <Route Component={AuthLogin} />
    </Route>

    <Route path="/logout" Component={AnonymousLayout}>
      <Route Component={AuthLogout} />
    </Route>

    <Route path="/forgot-password" Component={AnonymousLayout}>
      <Route Component={AuthForgotPassword} />
    </Route>

    <Route path="/reset-password" Component={AnonymousLayout}>
      <Route path=":token" Component={AuthResetPassword} />
    </Route>

    <Route path="/register" Component={AnonymousLayout}>
      <Route path=":token" Component={AuthRegister} />
    </Route>

    <Route path="/auth/deactivated">
      <Route render={() => <AuthErrorPage errorType={AUTH_ERROR_USER_DEACTIVATED} />} />
    </Route>

    <Route path="/403">
      <Route render={() => <HTTP403 />} />
    </Route>

    {/* TODO: admin layouts! */}
    <AuthenticatedRoute
      path="/admin"
      render={({ props, error }) => {
        const authErrorResult = handleApplicationErrors(props, error);
        if (authErrorResult) {
          return authErrorResult;
        }
        return <AdminLayout {...props} />;
      }}
      query={graphql`
        query routes_AdminRoot_Query {
          viewer {
            ...AdminLayout_viewer
          }
        }
      `}
    >
      <Redirect from="" to="/admin/properties" />
      <Route path="communities">
        <Route
          prepareVariables={(params) => ({ ...params, count: DEFAULT_LIST_PAGE_SIZE })}
          query={adminCommunityListRouteQuery}
          render={adminCommunityListRoute}
        />
        <Route
          path=":uuid"
          prepareVariables={(params) => ({ ...params, count: DEFAULT_LIST_PAGE_SIZE })}
          query={adminCommunityShowRouteQuery}
          render={adminCommunityShowRoute}
        />
      </Route>
      <Route path="meters">
        <Route
          prepareVariables={(params) => ({ ...params, count: DEFAULT_LIST_PAGE_SIZE })}
          query={adminMeterListRouteQuery}
          render={adminMeterListRoute}
        />
        <Route
          path=":uuid"
        >
          <Route
            query={adminMeterShowRouteQuery}
            render={adminMeterShowRoute}
          />
          <Route
            path="trade-rules"
            prepareVariables={(params, { location }) => {
              const {
                state, type, date, tz,
              } = location.query;

              const variables = { ...params, count: DEFAULT_LIST_PAGE_SIZE };

              if (state && TradeRuleStates.includes(state)) {
                variables.state = state;
              }
              if (type && TradeTypes.includes(type)) {
                variables.type = type;
              }

              let timeZone = APIConfig().DEFAULT_TIMEZONE;
              if (tz && tz !== undefined) {
                timeZone = tz;
              }
              if (date) {
                const d = DateTime.fromISO(date, { zone: timeZone });
                if (d.isValid) {
                  variables.start = d.startOf('day').toSeconds();
                  variables.finish = d.startOf('day').plus({ days: 1 }).toSeconds();
                }
              }

              return variables;
            }}
            query={adminMeterTradeRulesRouteQuery}
            render={adminMeterTradeRulesRoute}
          />
        </Route>
      </Route>
      <Route path="portfolios">
        <Route
          prepareVariables={(params) => ({ ...params, count: DEFAULT_LIST_PAGE_SIZE })}
          query={adminPortfolioListRouteQuery}
          render={adminPortfolioListRoute}
        />
        <Route
          path="new"
          render={({ props }) => (props ? <AdminPortfolioCreate {...props} /> : <Loading />)}
        />
        <Route path=":uuid">
          <Route
            query={adminPortfolioShowRouteQuery}
            render={adminPortfolioShowRoute}
          />
          <Route
            path="edit"
            query={adminPortfolioEditRouteQuery}
            render={adminPortfolioEditRoute}
          />
          <Route
            path="retire"
            query={adminPortfolioRetireRouteQuery}
            render={adminPortfolioRetireRoute}
          />
          <Route path="portfolio-user-member">
            <Route
              path="invite"
              prepareVariables={(params) => ({ ...params, count: 500 })}
              query={adminPortfolioUserMemberCreateRouteQuery}
              render={adminPortfolioUserMemberCreateRoute}
            />
          </Route>
          <Route path="portfolio-property-member">
            <Route
              path="add"
              prepareVariables={(params) => ({ ...params, count: DEFAULT_LIST_PAGE_SIZE })}
              query={adminPortfolioPropertyMemberCreateRouteQuery}
              render={adminPortfolioPropertyMemberCreateRoute}
            />
          </Route>
        </Route>
      </Route>
      <Route path="portfolio-property-members">
        <Route path=":uuid">
          <Route
            query={adminPortfolioPropertyMemberShowRouteQuery}
            render={adminPortfolioPropertyMemberShowRoute}
          />
          <Route
            path="retire"
            query={adminPortfolioPropertyMemberRetireRouteQuery}
            render={adminPortfolioPropertyMemberRetireRoute}
          />
        </Route>
      </Route>
      <Route path="portfolio-user-members">
        <Route path=":uuid">
          <Route
            query={adminPortfolioUserMemberShowRouteQuery}
            render={adminPortfolioUserMemberShowRoute}
          />
          <Route
            path="retire"
            query={adminRetirePortfolioUserMemberRouteQuery}
            render={adminRetirePortfolioUserMemberRoute}
          />
          <Route
            path="edit"
            query={adminPortfolioUserMemberEditRouteQuery}
            render={adminPortfolioUserMemberEditRoute}
          />
        </Route>
      </Route>
      <Route path="primary-billing-points">
        <Route
          prepareVariables={(params) => ({ ...params, count: DEFAULT_LIST_PAGE_SIZE })}
          query={adminPrimaryBillingPointListRouteQuery}
          render={adminPrimaryBillingPointListRoute}
        />
        <Route
          path=":uuid"
          query={adminPrimaryBillingPointShowRouteQuery}
          render={adminPrimaryBillingPointShowRoute}
        />
      </Route>
      <Route path="properties">
        <Route
          render={({ props }) => (props ? <AdminPropertyList {...props} /> : <Loading />)}
          prepareVariables={(params) => ({ ...params, count: DEFAULT_LIST_PAGE_SIZE })}
          query={graphql`
            query routes_AdminPropertyList_Query($count: Int, $cursor: String) {
              viewer {
                ...AdminPropertyList_viewer
              }
            }
          `}
        />
        <Route
          path=":uuid"
          render={({ props }) => (props ? <AdminPropertyShow {...props} /> : <Loading />)}
          query={graphql`
            query routes_AdminPropertyShow_Query($uuid: ID!) {
              property(uuid: $uuid) {
                ...AdminPropertyShow_property
              }
            }
          `}
        />
      </Route>
      <Route path="property-users">
        <Route
          prepareVariables={(params) => ({ ...params, count: DEFAULT_LIST_PAGE_SIZE })}
          query={adminPropertyUserListRouteQuery}
          render={adminPropertyUserListRoute}
        />
        <Route path="new">
          <Route
            path=""
            prepareVariables={(params) => ({ ...params, count: DEFAULT_LIST_PAGE_SIZE })}
            query={adminPropertyUserAddRouteQuery}
            render={adminPropertyUserAddRoute}
          />
        </Route>
        <Route
          path=":uuid"
          query={adminPropertyUserShowRouteQuery}
          render={adminPropertyUserShowRoute}
        />
      </Route>
      <Route path="standing-charges">
        <Route
          prepareVariables={(params) => ({ ...params, count: DEFAULT_LIST_PAGE_SIZE })}
          query={adminStandingChargeListRouteQuery}
          render={adminStandingChargeListRoute}
        />
        <Route
          path=":uuid"
          query={adminStandingChargeShowRouteQuery}
          render={adminStandingChargeShowRoute}
        />
      </Route>
      <Route path="tariffs">
        <Route
          prepareVariables={(params) => ({ ...params, count: DEFAULT_LIST_PAGE_SIZE })}
          query={adminTariffListRouteQuery}
          render={adminTariffListRoute}
        />
        <Route
          path=":uuid"
          query={adminTariffShowRouteQuery}
          render={adminTariffShowRoute}
        />
      </Route>
      <Route path="trade-rules">
        <Route
          prepareVariables={(params, { location }) => {
            const {
              state, type, date, tz,
            } = location.query;
            let timeZone = APIConfig().DEFAULT_TIMEZONE;
            if (tz && tz !== undefined) {
              timeZone = tz;
            }

            const variables = { ...params, count: DEFAULT_LIST_PAGE_SIZE };

            if (state && TradeRuleStates.includes(state)) {
              variables.state = state;
            }
            if (type && TradeTypes.includes(type)) {
              variables.type = type;
            }
            if (date) {
              const d = DateTime.fromISO(date, { zone: timeZone });
              if (d.isValid) {
                variables.start = d.startOf('day').toSeconds();
                variables.finish = d.startOf('day').plus({ days: 1 }).toSeconds();
              }
            }

            return variables;
          }}
          query={adminTradeRuleListRouteQuery}
          render={adminTradeRuleListRoute}
        />
        <Route
          path=":uuid"
          query={adminTradeRuleShowRouteQuery}
          render={adminTradeRuleShowRoute}
        />
      </Route>
      <Route path="users">
        <Route
          prepareVariables={(params) => ({ ...params, count: DEFAULT_LIST_PAGE_SIZE })}
          query={adminUserListRouteQuery}
          render={adminUserListRoute}
        />
        <Route
          path=":uuid"
          query={adminUserShowRouteQuery}
          render={adminUserShowRoute}
        />
        <Route path="invite">
          <Route
            path="admin"
            Component={AdminUserInviteAdmin}
          />
          <Route
            path="customer"
            Component={AdminUserInviteCustomer}
          />
        </Route>
      </Route>
      <Route path="volume-charges">
        <Route
          prepareVariables={(params) => ({ ...params, count: DEFAULT_LIST_PAGE_SIZE })}
          query={adminVolumeChargeListRouteQuery}
          render={adminVolumeChargeListRoute}
        />
        <Route
          path=":uuid"
          query={adminVolumeChargeShowRouteQuery}
          render={adminVolumeChargeShowRoute}
        />
      </Route>
    </AuthenticatedRoute>

    <AuthenticatedRoute
      path="/"
      Component={CustomerLayout}
      query={graphql`
        query routes_Root_Query {
          viewer {
            id viewerUser {
              uuid,
              type
            }
            ...CustomerLayout_viewer
          }
        }
      `}
      prepareVariables={(params, _others) => ({ ...params })}
      render={({ props, error }) => {
        const authErrorResult = handleApplicationErrors(props, error);
        if (authErrorResult) {
          return authErrorResult;
        }

        return <CustomerLayout {...props} />;
      }}
    >

      <Redirect from="" to="/properties" />

      <Route path="profile">
        <Redirect from="" to="/profile/settings" />
        <Route
          path="settings"
          query={profileSettingsRouteQuery}
          render={profileSettingsRoute}
        />
      </Route>

      <Route path="properties">
        <Route
          prepareVariables={(params) => ({ ...params, count: DEFAULT_LIST_PAGE_SIZE })}
          query={propertyListRouteQuery}
          render={propertyListRoute}
        />
      </Route>
      <Route path="portfolios">
        <Route
          prepareVariables={(params) => ({ ...params, count: DEFAULT_LIST_PAGE_SIZE })}
          query={portfolioListRouteQuery}
          render={portfolioListRoute}
        />
      </Route>

      <Route path="trade-rules">
        <Route
          prepareVariables={(params) => ({ ...params, first: 50 })}
          query={tradeRuleCustomerProposalsRouteQuery}
          render={tradeRuleCustomerProposalsRoute}
        />

        <Route
          path=":uuid"
          prepareVariables={(params) => ({ ...params, first: 50 })}
          query={tradeRuleCustomerShowRouteQuery}
          render={tradeRuleCustomerShowRoute}
        />
        <Route
          path="/propose/nominated"
        >
          <Route
            path=""
            query={tradeRuleProposalNominatedGeneralisedRouteQuery}
            render={tradeRuleProposalNominatedGeneralisedRoute}
          />
        </Route>
      </Route>
    </AuthenticatedRoute>

    <AuthenticatedRoute
      path="properties/:uuid"
      Component={CustomerPropertyLayout}
      query={graphql`
        query routes_CustomerPropertyRoot_Query($uuid: ID!) {
          viewer {
            ...CustomerPropertyLayout_viewer
          }
          property(uuid: $uuid) {
            ...CustomerPropertyLayout_property
          }
        }
      `}
      prepareVariables={(params, _others) => ({ ...params })}
      render={({ props, error }) => {
        const authErrorResult = handleApplicationErrors(props, error);
        if (authErrorResult) {
          return authErrorResult;
        }
        return <CustomerPropertyLayout {...props} />;
      }}
    >
      <Route
        path=""
        query={propertyShowRouteQuery}
        prepareVariables={(params) => ({ ...params })}
        render={propertyShowRoute}
      />
      <Route
        path="settings"
        query={propertySettingsRouteQuery}
        render={propertySettingsRoute}
      />

      <Route
        path="tariffs"
        query={tariffListRouteQuery}
        render={tariffListRoute}
      />
      <Route path="trade-rules">
        <Route
          path="active"
          query={tradeRuleActiveRouteQuery}
          render={tradeRuleActiveRoute}
        />
        <Route path="propose/nominated">
          <Route
            path=""
            prepareVariables={(params) => {
              const timezone = APIConfig().DEFAULT_TIMEZONE;

              return {
                ...params,
                now: Math.floor(DateTime.local({ zone: timezone }).toSeconds()),
              };
            }}
            query={tradeRuleProposeNominatedRouteQuery}
            render={tradeRuleProposeNominatedRoute}
          />
        </Route>
        <Route
          path=""
          query={tradeRuleListRouteQuery}
          render={tradeRuleListRoute}
        />
        <Route
          path=":ruleId"
          query={tradeRuleShowRouteQuery}
          render={tradeRuleShowRoute}
        />
      </Route>
      <Route path="meters/:meterId/trade-rules">
        <Route
          path="nominated/prioritize"
          query={tradeRulePrioritizeNominatedRouteQuery}
          render={tradeRulePrioritizeNominatedRoute}
        />
        <Route path="community/set">
          <Route
            path=""
            prepareVariables={(params) => {
              const timezone = APIConfig().DEFAULT_TIMEZONE;
              return {
                ...params,
                now: Math.floor(DateTime.local({ zone: timezone }).toSeconds()),
              };
            }}
            query={tradeRuleSetCommunityRouteQuery}
            render={tradeRuleSetCommunityRoute}
          />
        </Route>
      </Route>
      <Route path="trades">
        <Route
          path="history"
          query={tradeListRouteQuery}
          render={tradeListRoute}
        />
      </Route>
    </AuthenticatedRoute>

    <AuthenticatedRoute
      path="portfolios/:uuid"
      Component={CustomerPortfolioLayout}
      query={graphql`
        query routes_CustomerPortfolioRoot_Query($uuid: ID!) {
          viewer {
            ...CustomerPortfolioLayout_viewer
          }
          portfolio(uuid: $uuid) {
            ...CustomerPortfolioLayout_portfolio
          }
        }
      `}
      prepareVariables={(params, _others) => ({ ...params })}
      render={({ props, error }) => {
        const authErrorResult = handleApplicationErrors(props, error);
        if (authErrorResult) {
          return authErrorResult;
        }
        return (
          <CustomerPortfolioLayout
            // Force layout remount when switching portfolios to ensure full state re-initialization
            key={props.match.params.uuid}
            {...props}
          />
        );
      }}
    >
      <Route
        path=""
        query={portfolioShowRouteQuery}
        prepareVariables={(params) => ({ ...params })}
        render={portfolioShowRoute}
      />
      <Route
        path="properties"
        prepareVariables={(params, { location }) => (
          buildPortfolioPropertyMemberListRetryVariables({ match: { params, location } })
        )}
        query={portfolioPropertyMemberListRouteQuery}
        render={portfolioPropertyMemberListRoute}
      />
      <Route
        path="users"
        prepareVariables={(params, { location }) => (
          buildPortfolioUserMemberListRetryVariables({ match: { params, location } })
        )}
        query={portfolioUserMemberListRouteQuery}
        render={portfolioUserMemberListRoute}
      />
    </AuthenticatedRoute>
  </Route>,

);
